import { useCallback, useState } from "react";
import useUpdateTask from "@/modules/tasks/hooks/use-update-task.hook";
import { TaskStatusesBackend } from "@/modules/tasks/enums";
import { MixpanelTrackingUtil } from "@/modules/analytics/utils";
import { Task } from "@/modules/tasks/dtos";
import { useAnalyticsContext } from "@/modules/analytics/context/analytics.context";

interface UseTaskCompletionProps {
  queryKeys?: string[];
  successMessage?: string;
  onSuccess?: () => void;
  refetch?: () => void;
  enableOptimisticUpdates?: boolean;
}

export const useTaskCompletion = ({
  queryKeys = ["tasks-dashboard", "task-statistics", "tasks-count"],
  successMessage = "Task completed successfully",
  onSuccess,
  refetch,
  enableOptimisticUpdates = false,
}: UseTaskCompletionProps = {}) => {
  const { analytics, userInfo } = useAnalyticsContext();
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [optimisticallyCompletedTasks, setOptimisticallyCompletedTasks] =
    useState<Set<string>>(new Set());

  const updateTaskMutation = useUpdateTask({
    queryKeys,
    successMessage,
    onSuccess: () => {
      onSuccess?.();
      refetch?.();
    },
  });

  const handleMarkComplete = useCallback(
    (taskUid: string, task?: Task, source?: string) => {
      if (enableOptimisticUpdates) {
        setOptimisticallyCompletedTasks((prev) => new Set(prev).add(taskUid));
      }
      setCompletingTaskId(taskUid);

      updateTaskMutation.mutate(
        {
          id: taskUid,
          status: TaskStatusesBackend.completed,
        },
        {
          onSuccess: () => {
            if (analytics && task && source) {
              MixpanelTrackingUtil.trackTaskCompletion(
                analytics,
                task,
                source,
                userInfo
              );
            }

            if (enableOptimisticUpdates && refetch) {
              // Refetch tasks list after backend confirms completion
              setTimeout(() => {
                refetch();
                setOptimisticallyCompletedTasks((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(taskUid);
                  return newSet;
                });
              }, 500); // Small delay to show the completed state
            }
          },
          onError: () => {
            // Revert optimistic update on error
            if (enableOptimisticUpdates) {
              setOptimisticallyCompletedTasks((prev) => {
                const newSet = new Set(prev);
                newSet.delete(taskUid);
                return newSet;
              });
            }
          },
          onSettled: () => {
            setCompletingTaskId(null);
          },
        }
      );
    },
    [updateTaskMutation, refetch, enableOptimisticUpdates, analytics, userInfo]
  );

  const isTaskOptimisticallyCompleted = useCallback(
    (taskUid: string) => {
      return (
        enableOptimisticUpdates && optimisticallyCompletedTasks.has(taskUid)
      );
    },
    [optimisticallyCompletedTasks, enableOptimisticUpdates]
  );

  return {
    handleMarkComplete,
    completingTaskId,
    isTaskOptimisticallyCompleted,
    optimisticallyCompletedTasks,
    isLoading: updateTaskMutation.isPending,
  };
};
